import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { INFO_BLUE, PRIMARY_COLOR, SECONDARY_COLOR } from '../../appearance/Colors';
import { CHANGELOG_ENTRIES } from '../../data/changelogEntries';
import LayoutDefault from '../Landing/layouts/LayoutDefault';
import { scrollToSection } from '../Landing/utils/navigationUtils';

const renderReleaseMedia = media => {
    if (!media) {
        return null;
    }

    if (media.type === 'video') {
        return (
            <ReleaseFigure>
                <video controls preload="metadata">
                    <source src={media.src} />
                </video>
                {media.caption ? <figcaption>{media.caption}</figcaption> : null}
            </ReleaseFigure>
        );
    }

    return (
        <ReleaseFigure>
            <img src={media.src} alt={media.alt || ''} loading="lazy" />
            {media.caption ? <figcaption>{media.caption}</figcaption> : null}
        </ReleaseFigure>
    );
};

const ChangelogPage = () => {
    const location = useLocation();

    useEffect(() => {
        if (!location.hash || !location.hash.startsWith('#')) {
            return undefined;
        }

        const sectionId = decodeURIComponent(location.hash.substring(1));
        const timer = setTimeout(() => {
            scrollToSection(sectionId, 96);
        }, 100);

        return () => clearTimeout(timer);
    }, [location.hash]);

    return (
        <LayoutDefault>
            <Page>
                <PageHeader>
                    <Eyebrow>Product updates</Eyebrow>
                    <h1>Changelog</h1>
                    <Intro>New features, thoughtful improvements, and fixes from Luckynote.</Intro>
                </PageHeader>

                <ReleaseList>
                    {CHANGELOG_ENTRIES.map((entry, index) => (
                        <Release key={entry.id} id={entry.id}>
                            <ReleaseDate>
                                <time dateTime={entry.date}>{entry.dateLabel}</time>
                                {index === 0 ? <LatestLabel>Latest</LatestLabel> : null}
                            </ReleaseDate>
                            <ReleaseContent>
                                <h2>{entry.title}</h2>
                                <Summary>{entry.summary}</Summary>
                                {renderReleaseMedia(entry.media)}
                                <ChangeList>
                                    {entry.changes.map(change => (
                                        <li key={change}>{change}</li>
                                    ))}
                                </ChangeList>
                            </ReleaseContent>
                        </Release>
                    ))}
                </ReleaseList>
            </Page>
        </LayoutDefault>
    );
};

const Page = styled.div`
    width: min(1040px, calc(100% - 48px));
    min-height: 70vh;
    margin: 0 auto;
    padding: 88px 0 120px;
    color: ${PRIMARY_COLOR};

    @media (max-width: 720px) {
        width: min(100% - 32px, 1040px);
        padding: 56px 0 80px;
    }
`;

const PageHeader = styled.header`
    max-width: 680px;
    margin: 0 auto;
    text-align: center;

    h1 {
        margin: 10px 0 16px;
        color: ${PRIMARY_COLOR};
        font-size: 52px;
        line-height: 1.08;
        letter-spacing: 0;
    }

    @media (max-width: 720px) {
        h1 {
            font-size: 40px;
        }
    }
`;

const Eyebrow = styled.div`
    color: ${INFO_BLUE};
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
`;

const Intro = styled.p`
    margin: 0;
    color: ${SECONDARY_COLOR};
    font-size: 18px;
    line-height: 1.6;
`;

const ReleaseList = styled.div`
    margin-top: 104px;

    @media (max-width: 720px) {
        margin-top: 64px;
    }
`;

const Release = styled.article`
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 56px;
    padding: 0 0 96px;

    &:last-child {
        padding-bottom: 0;
    }

    & + & {
        padding-top: 96px;
        border-top: 1px solid rgba(35, 39, 42, 0.12);
    }

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
        gap: 20px;
        padding-bottom: 72px;

        & + & {
            padding-top: 72px;
        }
    }
`;

const ReleaseDate = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding-top: 8px;
    color: rgba(35, 39, 42, 0.54);
    font-size: 14px;

    @media (max-width: 720px) {
        flex-direction: row;
        align-items: center;
        padding-top: 0;
    }
`;

const ReleaseContent = styled.div`
    min-width: 0;

    h2 {
        max-width: 760px;
        margin: 0 0 18px;
        color: ${PRIMARY_COLOR};
        font-size: 36px;
        font-weight: 500;
        line-height: 1.2;
        letter-spacing: 0;
    }

    @media (max-width: 720px) {
        h2 {
            font-size: 29px;
        }
    }
`;

const LatestLabel = styled.span`
    padding: 3px 7px;
    border: 1px solid rgba(0, 133, 202, 0.32);
    border-radius: 4px;
    color: ${INFO_BLUE};
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
`;

const Summary = styled.p`
    max-width: 700px;
    margin: 0 0 32px;
    color: ${SECONDARY_COLOR};
    font-size: 18px;
    line-height: 1.7;
`;

const ReleaseFigure = styled.figure`
    margin: 0 0 32px;

    img,
    video {
        display: block;
        width: 100%;
        border: 1px solid rgba(35, 39, 42, 0.12);
        border-radius: 8px;
        background: #eef2f5;
    }

    img {
        height: auto;
    }

    video {
        aspect-ratio: 16 / 9;
        object-fit: contain;
    }

    figcaption {
        margin-top: 10px;
        color: rgba(35, 39, 42, 0.54);
        font-size: 13px;
        line-height: 1.5;
    }
`;

const ChangeList = styled.ul`
    max-width: 700px;
    margin: 0;
    padding-left: 20px;
    list-style: disc outside;

    li {
        display: list-item;
        padding-left: 6px;
        color: ${SECONDARY_COLOR};
        font-size: 16px;
        line-height: 1.65;
    }

    li + li {
        margin-top: 8px;
    }

    li::marker {
        color: ${INFO_BLUE};
    }
`;

export default ChangelogPage;
